<template>
  <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="width: 640px; max-width: 90vw">
      <q-card-section class="row items-center no-wrap">
        <div>
          <div class="text-h6">Configurações da Venda Online</div>
          <div class="text-caption text-grey-7">Horário de corte, estoque, meios de pagamento e o destino da venda no financeiro</div>
        </div>
        <q-space />
        <q-btn icon="close" flat round dense @click="$emit('update:modelValue', false)" />
      </q-card-section>

      <q-separator />

      <q-banner v-if="erroCarregamento" dense class="bg-red-1 text-negative" data-test="erro-carregar-configuracao">
        {{ erroCarregamento }}
        <template #action>
          <q-btn flat dense no-caps color="negative" label="Tentar de novo" data-test="recarregar-configuracao" @click="load" />
        </template>
      </q-banner>

      <q-card-section class="q-gutter-y-md scroll" style="max-height: 70vh" data-test="corpo-configuracao">
        <div class="text-subtitle2">Horário de corte</div>
        <div class="text-caption text-grey-7" data-test="ajuda-corte">
          Horário de Brasília, em 24h. O link vale até o corte da loteria; com vários produtos, vale o corte mais cedo. O corte vai até
          {{ corteMaximo }}, uma hora antes do limite do concurso ({{ limiteConcurso }}).
        </div>

        <div>
          <div class="text-caption text-grey-8 q-mb-xs">Loterias vendidas online</div>
          <q-markup-table flat bordered dense data-test="tabela-horarios">
            <thead>
              <tr>
                <th class="text-left">Loteria</th>
                <th class="text-left">Horário de corte</th>
                <th class="text-center">Vende online</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="horario in horarios" :key="horario.loteria_id" :data-test="`horario-${horario.loteria_id}`">
                <td>{{ horario.loteria_nome }}</td>
                <td style="width: 220px">
                  <div class="row items-start no-wrap">
                    <q-input
                      v-model="horario.horario_corte"
                      dense
                      mask="time"
                      class="col"
                      :rules="['time']"
                      :disable="!horario.ativo"
                      :error="!!erroDoCorte(horario)"
                      :error-message="erroDoCorte(horario)"
                      :data-test="`horario-corte-${horario.loteria_id}`"
                    >
                      <template #append>
                        <q-icon name="access_time" class="cursor-pointer">
                          <q-popup-proxy transition-show="scale" transition-hide="scale">
                            <q-time v-model="horario.horario_corte" format24h />
                          </q-popup-proxy>
                        </q-icon>
                      </template>
                    </q-input>
                    <q-btn
                      flat
                      dense
                      round
                      icon="content_copy"
                      :disable="!horario.ativo"
                      :data-test="`copiar-todas-${horario.loteria_id}`"
                      @click="copyParaTodas(horario)"
                    >
                      <q-tooltip>Copiar para todas</q-tooltip>
                    </q-btn>
                  </div>
                </td>
                <td class="text-center">
                  <q-toggle v-model="horario.ativo" :data-test="`horario-ativo-${horario.loteria_id}`" />
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </div>

        <q-separator />

        <div class="text-subtitle2">Estoque</div>
        <q-option-group v-model="opcoes.vendaonline_estoque_modo" :options="modosEstoque" />
        <div v-if="fieldError('vendaonline_estoque_modo')" class="text-negative text-caption">{{ fieldError("vendaonline_estoque_modo") }}</div>
        <div class="text-caption text-grey-7">A reserva volta sozinha ao estoque quando a venda cancela ou expira.</div>

        <q-separator />

        <div class="text-subtitle2">Meios de pagamento</div>
        <div class="text-caption text-grey-7">O primeiro da lista é o preferencial. Use as setas para mudar a ordem.</div>
        <q-list bordered separator data-test="lista-meios">
          <q-item v-for="(meio, indice) in meios" :key="meio.meio" :data-test="`meio-linha-${meio.meio}`">
            <q-item-section>
              <q-item-label>
                {{ rotuloDoMeio(meio.meio) }}
                <q-badge v-if="indice === 0" color="primary" class="q-ml-sm">Preferencial</q-badge>
              </q-item-label>
              <q-item-label caption>{{ isDisponivel(meio.meio) ? ajudaDoMeio(meio.meio) : "Indisponível no momento" }}</q-item-label>
            </q-item-section>
            <q-item-section side>
              <div class="row items-center no-wrap">
                <q-btn
                  flat
                  dense
                  icon="keyboard_arrow_up"
                  :disable="indice === 0"
                  :data-test="`meio-subir-${meio.meio}`"
                  @click="changeMeios(indice, indice - 1)"
                >
                  <q-tooltip>Subir</q-tooltip>
                </q-btn>
                <q-btn
                  flat
                  dense
                  icon="keyboard_arrow_down"
                  :disable="indice === meios.length - 1"
                  :data-test="`meio-descer-${meio.meio}`"
                  @click="changeMeios(indice, indice + 1)"
                >
                  <q-tooltip>Descer</q-tooltip>
                </q-btn>
                <q-toggle v-model="meio.ativo" :disable="meio.meio === 'cartao' || !isDisponivel(meio.meio)" :data-test="`meio-ativo-${meio.meio}`" />
              </div>
            </q-item-section>
          </q-item>
        </q-list>
        <div v-if="erroDosMeios" class="text-negative text-caption" data-test="erro-meios">{{ erroDosMeios }}</div>

        <template v-if="gateways.length">
          <div class="text-subtitle2">Forma de cobrança</div>
          <div class="text-caption text-grey-7">Só a equipe DouraSoft vê e altera. Vendas já geradas continuam na forma de cobrança em que nasceram.</div>
          <q-option-group v-model="opcoes.vendaonline_gateway" :options="gatewayOptions" data-test="gateway" />
          <div v-if="fieldError('vendaonline_gateway')" class="text-negative text-caption">{{ fieldError("vendaonline_gateway") }}</div>
        </template>

        <q-separator />

        <div class="text-subtitle2">Limite de valor</div>
        <div class="text-caption text-grey-7" data-test="ajuda-limite">
          Obrigatório. O link acima do limite é recusado, para evitar compra alta que depois é contestada no banco como fraude.
        </div>
        <div class="row q-col-gutter-md">
          <div class="col-12 col-sm-6">
            <campo-valor
              v-model="limite.venda"
              label="Valor máximo por venda (com a taxa)"
              data-test="limite-venda"
              :error="!!erroDoLimite('venda')"
              :error-message="erroDoLimite('venda')"
            />
          </div>
          <div v-if="mudou('venda')" class="col-12 col-sm-6">
            <campo-valor v-model="limite.vendaConfirmacao" label="Digite o valor de novo" data-test="limite-venda-confirmacao" />
          </div>
        </div>
        <div class="row q-col-gutter-md">
          <div class="col-12 col-sm-6">
            <campo-valor
              v-model="limite.clienteDia"
              label="Máximo por cliente no dia"
              data-test="limite-cliente-dia"
              :error="!!erroDoLimite('clienteDia')"
              :error-message="erroDoLimite('clienteDia')"
            />
          </div>
          <div v-if="mudou('clienteDia')" class="col-12 col-sm-6">
            <campo-valor v-model="limite.clienteDiaConfirmacao" label="Digite o valor de novo" data-test="limite-cliente-dia-confirmacao" />
          </div>
        </div>
        <div class="text-caption text-grey-7">O máximo por cliente soma os links do dia do mesmo cliente, menos os cancelados e expirados. Em branco, sem limite.</div>

        <q-separator />

        <div class="text-subtitle2">Taxa de conveniência</div>
        <div class="text-caption text-grey-7">
          Taxa que a lotérica cobra do cliente, somada ao valor da venda e recebida por você. Não é a tarifa da DouraSoft.
        </div>
        <div class="row q-gutter-md" data-test="taxa-modos">
          <q-radio
            v-for="modo in modosTaxa"
            :key="modo.value"
            v-model="taxa.modo"
            :val="modo.value"
            :label="modo.label"
            :data-test="`taxa-modo-${modo.value}`"
          />
        </div>
        <div class="text-caption text-grey-7" data-test="ajuda-taxa-modo">{{ ajudaDoModoDaTaxa }}</div>

        <template v-if="taxa.modo === 'editavel'">
          <campo-valor
            v-model="taxa.limite"
            label="Editável em vendas acima de"
            data-test="taxa-limite"
            :error="!!fieldError('vendaonline_taxa_conveniencia_limite')"
            :error-message="fieldError('vendaonline_taxa_conveniencia_limite')"
          />
          <div class="text-caption text-grey-7">R$ 0,00 permite alterar em qualquer venda. O operador precisa da permissão Alterar taxa de conveniência em Usuários.</div>
        </template>

        <template v-if="taxa.modo !== 'isenta'">
          <q-markup-table flat bordered dense data-test="tabela-faixas">
            <thead>
              <tr>
                <th class="text-left">Venda a partir de</th>
                <th class="text-left">Pix</th>
                <th class="text-left">Boleto/Pix</th>
                <th class="text-left">Cartão</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr v-if="!taxa.faixas.length">
                <td colspan="5" class="text-grey-7">Sem faixas, nenhuma taxa é cobrada.</td>
              </tr>
              <tr v-for="(faixa, indice) in taxa.faixas" :key="faixa.chave" :data-test="`faixa-linha-${indice}`">
                <td v-for="coluna in COLUNAS_DA_FAIXA" :key="coluna">
                  <campo-valor
                    v-model="faixa[coluna]"
                    :label="ROTULOS_DA_FAIXA[coluna]"
                    :data-test="`faixa-${indice}-${coluna}`"
                    :error="tentouSalvar && faixa[coluna] === null"
                  />
                </td>
                <td class="text-right">
                  <q-btn flat dense round icon="delete" :data-test="`faixa-remover-${indice}`" @click="removeFaixa(indice)">
                    <q-tooltip>Remover faixa</q-tooltip>
                  </q-btn>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
          <q-btn flat dense no-caps color="primary" icon="add" label="Adicionar faixa" data-test="faixa-adicionar" @click="addFaixa" />
          <div v-if="erroDasFaixas" class="text-negative text-caption" data-test="erro-faixas">{{ erroDasFaixas }}</div>
        </template>

        <q-separator />

        <div class="text-subtitle2">Financeiro</div>
        <div class="text-caption text-grey-7">
          A conta que recebe a venda vem de Cadastros Financeiros › Formas de Cobrança. A venda paga lança nessa conta a receita e a despesa da tarifa, na data do crédito.
        </div>
        <q-select
          v-model="opcoes.vendaonline_plano_receita_id"
          dense
          emit-value
          map-options
          label="Plano de contas da receita"
          hint="Receita bruta da venda no DRE"
          data-test="plano-receita"
          :clearable="false"
          :options="planosReceitaOptions"
          :error="!!fieldError('vendaonline_plano_receita_id')"
          :error-message="fieldError('vendaonline_plano_receita_id')"
        />
        <q-select
          v-model="opcoes.vendaonline_plano_despesa_id"
          dense
          emit-value
          map-options
          label="Plano de contas da despesa com tarifas"
          hint="Sugestão: Tarifas de cobrança"
          data-test="plano-despesa"
          :options="planosDespesaOptions"
          :error="!!fieldError('vendaonline_plano_despesa_id')"
          :error-message="fieldError('vendaonline_plano_despesa_id')"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat no-caps label="Cancelar" data-test="cancelar" @click="$emit('update:modelValue', false)" />
        <q-btn
          unelevated
          no-caps
          color="primary"
          label="Salvar configurações"
          data-test="salvar"
          :loading="salvando || isVendaOnlineSaving"
          :disable="carregando || Boolean(erroCarregamento)"
          @click="save"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "@/axios-auth";
import CampoValor from "@/components/VendasOnline/campo-valor.vue";

const CAMPOS = ["vendaonline_estoque_modo", "vendaonline_plano_despesa_id", "vendaonline_plano_receita_id"];

const MEIOS = {
  pix: { rotulo: "Pix", ajuda: "" },
  boleto_pix: { rotulo: "Boleto/Pix", ajuda: "Mesmo preço do Pix" },
  cartao: { rotulo: "Cartão", ajuda: "Em breve" }
};

const COLUNAS_DA_FAIXA = ["a_partir_de", "pix", "boleto_pix", "cartao"];

const ROTULOS_DA_FAIXA = { a_partir_de: "A partir de", pix: "Pix", boleto_pix: "Boleto/Pix", cartao: "Cartão" };

const MODOS_DA_TAXA = [
  { value: "isenta", label: "Isenta", ajuda: "Nunca cobra a taxa de conveniência." },
  { value: "obrigatoria", label: "Obrigatória", ajuda: "Cobra sempre a taxa da faixa da venda, sem exceção." },
  { value: "editavel", label: "Editável", ajuda: "Cobra a taxa da faixa, e o operador com permissão pode alterar o valor nas vendas acima do limite." }
];

const valorOuNulo = (valor) => (typeof valor === "number" && Number.isFinite(valor) ? valor : null);

const LIMITES = {
  venda: { opcao: "vendaonline_valor_maximo_venda", confirmacao: "vendaConfirmacao" },
  clienteDia: { opcao: "vendaonline_valor_maximo_cliente_dia", confirmacao: "clienteDiaConfirmacao" }
};

const limiteSalvo = (valor) => (valor === "" || valor === null || valor === undefined ? null : valorOuNulo(Number(valor)));

const limitePositivo = (valor) => (valor === 0 ? null : valor);

const toFaixa = (faixa, chave) => ({
  chave,
  a_partir_de: valorOuNulo(faixa.a_partir_de),
  pix: valorOuNulo(faixa.pix),
  boleto_pix: valorOuNulo(faixa.boleto_pix),
  cartao: valorOuNulo(faixa.cartao)
});

const faixaCompleta = (faixa) => COLUNAS_DA_FAIXA.every((coluna) => faixa[coluna] !== null);

const toSelecao = (lista) => (lista || []).map((item) => ({ label: item.titulo, value: String(item.id) }));

const horaParaMinutos = (hora) => {
  const [h, m] = String(hora || "").split(":");

  return Number(h) * 60 + Number(m);
};

export default {
  name: "vendas-online-modal-configuracao",

  components: { CampoValor },
  emits: ["update:modelValue"],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      opcoes: {},
      horarios: [],
      meios: [],
      taxa: { modo: "isenta", limite: 0, faixas: [] },
      limite: { venda: null, vendaConfirmacao: null, clienteDia: null, clienteDiaConfirmacao: null },
      limiteSalvo: { venda: null, clienteDia: null },
      tentouSalvar: false,
      proximaChave: 0,
      COLUNAS_DA_FAIXA,
      ROTULOS_DA_FAIXA,
      limites: { limite_concurso: "19:00", corte_maximo: "18:00" },
      planosDespesa: [],
      planosReceita: [],
      salvando: false,
      carregando: false,
      erroCarregamento: null,
      modosTaxa: MODOS_DA_TAXA,
      modosEstoque: [
        { label: "Reservar a cota ao gerar o link", value: "bloqueio" },
        { label: "Não reservar", value: "livre" }
      ]
    };
  },
  computed: {
    ...mapGetters(["vendaOnlineConfiguracao", "vendaOnlineErros", "isVendaOnlineSaving"]),
    corteMaximo() {
      return this.limites.corte_maximo;
    },
    limiteConcurso() {
      return this.limites.limite_concurso;
    },
    planosDespesaOptions() {
      return toSelecao(this.planosDespesa);
    },
    planosReceitaOptions() {
      return toSelecao(this.planosReceita);
    },
    meiosDisponiveis() {
      const disponiveis = this.vendaOnlineConfiguracao && this.vendaOnlineConfiguracao.meios_disponiveis;

      return Array.isArray(disponiveis) ? disponiveis : ["pix", "boleto_pix"];
    },
    gateways() {
      return (this.vendaOnlineConfiguracao && this.vendaOnlineConfiguracao.gateways) || [];
    },
    gatewayOptions() {
      return this.gateways.map((gateway) => ({ label: gateway.nome, value: gateway.valor }));
    },
    limitesComErro() {
      return Object.keys(LIMITES).some((campo) => this.erroDoLimite(campo));
    },
    ajudaDoModoDaTaxa() {
      return (MODOS_DA_TAXA.find((modo) => modo.value === this.taxa.modo) || MODOS_DA_TAXA[0]).ajuda;
    },
    faixasIncompletas() {
      return this.taxa.modo !== "isenta" && this.taxa.faixas.some((faixa) => !faixaCompleta(faixa));
    },
    erroDasFaixas() {
      const erro = this.fieldError("vendaonline_taxa_conveniencia_faixas");
      if (erro) return erro;

      return this.tentouSalvar && this.faixasIncompletas ? "Preencha todas as colunas de cada faixa: a partir de quanto e a taxa de Pix, Boleto/Pix e Cartão." : "";
    },
    erroDosMeios() {
      const erro = this.fieldError("vendaonline_meios");
      if (erro) return erro;

      const semAtivo = !this.meios.some((meio) => meio.meio !== "cartao" && this.isDisponivel(meio.meio) && meio.ativo);

      return this.meios.length && semAtivo ? "Deixe ao menos um meio ativo." : "";
    }
  },
  watch: {
    modelValue(aberto) {
      if (!aberto) {
        return;
      }

      this.load();
    },
    vendaOnlineConfiguracao(configuracao) {
      this.applyConfiguracao(configuracao);
    }
  },
  methods: {
    applyConfiguracao(configuracao) {
      this.opcoes = configuracao ? { ...configuracao.opcoes } : {};
      const meios = this.opcoes.vendaonline_meios;
      this.meios = Array.isArray(meios) ? meios.map((meio) => ({ ...meio })) : [];
      const faixas = Array.isArray(this.opcoes.vendaonline_taxa_conveniencia_faixas) ? this.opcoes.vendaonline_taxa_conveniencia_faixas : [];
      this.taxa = {
        modo: this.opcoes.vendaonline_taxa_conveniencia_modo || "isenta",
        limite: valorOuNulo(Number(this.opcoes.vendaonline_taxa_conveniencia_limite)) ?? 0,
        faixas: faixas.map((faixa) => toFaixa(faixa, this.proximaChave++))
      };
      this.limiteSalvo = {
        venda: limiteSalvo(this.opcoes.vendaonline_valor_maximo_venda),
        clienteDia: limiteSalvo(this.opcoes.vendaonline_valor_maximo_cliente_dia)
      };
      this.limite = { venda: this.limiteSalvo.venda, vendaConfirmacao: null, clienteDia: this.limiteSalvo.clienteDia, clienteDiaConfirmacao: null };
      this.tentouSalvar = false;
    },
    addFaixa() {
      this.taxa.faixas.push(toFaixa({}, this.proximaChave++));
    },
    removeFaixa(indice) {
      this.taxa.faixas.splice(indice, 1);
    },
    async load() {
      this.applyConfiguracao(this.vendaOnlineConfiguracao);
      this.carregando = true;
      this.erroCarregamento = null;

      const [configuracao, horarios, despesas, receitas] = await Promise.all([
        this.$store.dispatch("fetchVendaOnlineConfiguracao"),
        this.$store.dispatch("fetchVendaOnlineHorarios"),
        axios.get("/plano-contas?cd=D"),
        axios.get("/plano-contas?cd=C")
      ]);
      this.carregando = false;
      if (!configuracao || !configuracao.ok) {
        this.erroCarregamento = "Não foi possível carregar a configuração da Venda Online. Tente de novo antes de salvar.";
      }

      if (horarios && horarios.ok) {
        this.horarios = horarios.horarios.map((horario) => ({ ...horario }));
        if (horarios.meta) this.limites = { ...this.limites, ...horarios.meta };
      }
      if (!despesas.error && despesas.data) this.planosDespesa = despesas.data.planodecontas || [];
      if (!receitas.error && receitas.data) this.planosReceita = receitas.data.planodecontas || [];
    },
    fieldError(campo) {
      const erro = this.vendaOnlineErros && this.vendaOnlineErros[campo];

      if (!erro) return "";
      if (typeof erro === "string") return erro;

      return Object.values(erro)[0];
    },
    isDisponivel(meio) {
      return meio === "cartao" || this.meiosDisponiveis.includes(meio);
    },
    mudou(campo) {
      return limitePositivo(this.limite[campo]) !== this.limiteSalvo[campo];
    },
    erroDoLimite(campo) {
      const erro = this.fieldError(LIMITES[campo].opcao);
      if (erro) return erro;
      if (!this.tentouSalvar || !this.mudou(campo)) return "";
      if (campo === "venda" && limitePositivo(this.limite.venda) === null) return "Informe o valor máximo por venda.";
      if (limitePositivo(this.limite[campo]) !== null && this.limite[campo] !== this.limite[LIMITES[campo].confirmacao]) {
        return "Digite o mesmo valor duas vezes para confirmar.";
      }

      return "";
    },
    rotuloDoMeio(meio) {
      return MEIOS[meio] ? MEIOS[meio].rotulo : meio;
    },
    ajudaDoMeio(meio) {
      return MEIOS[meio] ? MEIOS[meio].ajuda : "";
    },
    changeMeios(de, para) {
      const lista = [...this.meios];
      [lista[de], lista[para]] = [lista[para], lista[de]];
      this.meios = lista;
    },
    erroDoCorte(horario) {
      if (!horario.ativo || !/^\d{2}:\d{2}$/.test(horario.horario_corte || "")) return "";
      if (horaParaMinutos(horario.horario_corte) <= horaParaMinutos(this.corteMaximo)) return "";

      return `O corte vai até ${this.corteMaximo}, uma hora antes do limite do concurso (${this.limiteConcurso}).`;
    },
    copyParaTodas(origem) {
      this.horarios.forEach((horario) => {
        horario.horario_corte = origem.horario_corte;
      });
    },
    horariosParaSalvar() {
      return this.horarios
        .filter((horario) => horario.cadastrado || horario.ativo || horario.horario_corte !== horario.sugestao)
        .map(({ loteria_id, horario_corte, ativo }) => ({ loteria_id, horario_corte, ativo }));
    },
    notify(mensagem, cor) {
      this.$q.notify({ message: mensagem, color: cor, position: "top-right" });
    },
    async save() {
      if (this.carregando || this.erroCarregamento) return;
      this.tentouSalvar = true;
      if (this.horarios.some((horario) => this.erroDoCorte(horario)) || this.erroDosMeios || this.faixasIncompletas || this.limitesComErro) {
        this.notify("Corrija os campos em vermelho antes de salvar.", "orange");
        return;
      }

      this.salvando = true;

      const horarios = this.horariosParaSalvar();
      if (horarios.length) {
        const gravados = await this.$store.dispatch("saveVendaOnlineHorarios", horarios);
        if (!gravados || !gravados.ok) {
          this.salvando = false;
          this.notify((gravados && gravados.mensagem) || "Não foi possível salvar os horários de corte.", "negative");
          return;
        }
        this.horarios = gravados.horarios.map((horario) => ({ ...horario }));
      }

      const opcoes = {};
      CAMPOS.forEach((chave) => {
        const valor = this.opcoes[chave];
        opcoes[chave] = valor === null || valor === undefined ? "" : valor;
      });
      opcoes.vendaonline_meios = this.meios.map(({ meio, ativo }) => ({ meio, ativo }));
      opcoes.vendaonline_taxa_conveniencia_modo = this.taxa.modo;
      opcoes.vendaonline_taxa_conveniencia_limite = this.taxa.limite ?? 0;
      opcoes.vendaonline_taxa_conveniencia_faixas = this.taxa.faixas
        .filter(faixaCompleta)
        .map(({ a_partir_de, pix, boleto_pix, cartao }) => ({ a_partir_de, pix, boleto_pix, cartao }));
      Object.entries(LIMITES).forEach(([campo, { opcao, confirmacao }]) => {
        const valor = limitePositivo(this.limite[campo]);
        opcoes[opcao] = valor === null ? "" : valor;
        if (this.mudou(campo)) opcoes[`${opcao}_confirmacao`] = this.limite[confirmacao] ?? "";
      });
      if (this.gateways.length && this.opcoes.vendaonline_gateway) opcoes.vendaonline_gateway = this.opcoes.vendaonline_gateway;

      const resposta = await this.$store.dispatch("saveVendaOnlineConfiguracao", opcoes);
      this.salvando = false;

      const ok = !!(resposta && resposta.ok);
      this.notify(ok ? "Configuração da Venda Online salva." : (resposta && resposta.mensagem) || "Não foi possível salvar.", ok ? "green" : "negative");

      if (ok) {
        this.$emit("update:modelValue", false);
      }
    }
  }
};
</script>
